import { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useTranslate } from "@tolgee/react";
import useVoiceRecorder, { RECORDER_PHASE } from "../../hooks/useVoiceRecorder";

const VOICE_OPTION = {
  MY_VOICE: "my_voice",
  DEFAULT: "default",
};

const GENDER = {
  MALE: "male",
  FEMALE: "female",
  NON_BINARY: "non-binary",
};

const GENDER_OPTIONS = [
  { value: GENDER.MALE, emoji: "👨", labelKey: "voice_cloning.male" },
  { value: GENDER.FEMALE, emoji: "👩", labelKey: "voice_cloning.female" },
  { value: GENDER.NON_BINARY, emoji: "🧑", labelKey: "voice_cloning.neutral" },
];


const VoiceCloneRecorder = ({ onComplete, onSetupLater, defaultGender, onPreviewGender, startWithDefaultVoice, isSaveable, myVoicePhase, isPreviewPlaying, onTogglePreviewPlay, onSoundsGood, onReRecord, initialConsent = false, onConsentChange }) => {
  const { t } = useTranslate();
  const [voiceOption, setVoiceOption] = useState(startWithDefaultVoice ? VOICE_OPTION.DEFAULT : VOICE_OPTION.MY_VOICE);
  const [selectedGender, setSelectedGender] = useState(defaultGender || GENDER.NON_BINARY);
  const [previewingGender, setPreviewingGender] = useState(null);
  const [saved, setSaved] = useState(!!defaultGender);

  useEffect(() => {
    if (defaultGender && defaultGender !== selectedGender) {
      setSelectedGender(defaultGender);
      setSaved(true);
    }
  }, [defaultGender]);
  const [consentChecked, setConsentChecked] = useState(initialConsent);
  const previewAudioRef = useRef(null);

  const {
    phase,
    countdownValue,
    elapsedSeconds,
    audioBlob,
    start,
    stop,
    reset,
    canStop,
    maxSeconds,
  } = useVoiceRecorder();

  const isMyVoice = voiceOption === VOICE_OPTION.MY_VOICE;
  const isDone = phase === RECORDER_PHASE.DONE;

  const handleOptionChange = (option) => {
    setVoiceOption(option);
    reset();
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
    setSaved(false);
  };

  const handleMicClick = () => {
    if (phase === RECORDER_PHASE.IDLE || phase === RECORDER_PHASE.ERROR) {
      start();
    } else if (phase === RECORDER_PHASE.RECORDING && canStop) {
      stop();
    }
  };

  useEffect(() => {
    if (isDone && isMyVoice) {
      onComplete(audioBlob);
    }
  }, [isDone]);

  const handleContinue = () => {
    if (isSaveable) setSaved(true);
    onComplete(null, selectedGender);
  };

  const handlePreviewGender = (gender) => {
    if (!onPreviewGender || previewingGender !== null) return;
    setPreviewingGender(gender);
    onPreviewGender(
      gender,
      (blob) => {
        const url = URL.createObjectURL(blob);
        if (previewAudioRef.current) {
          previewAudioRef.current.src = url;
          previewAudioRef.current.play().catch(() => setPreviewingGender(null));
        } else {
          setPreviewingGender(null);
        }
      },
      () => setPreviewingGender(null)
    );
  };

  return (
    <Container>
      <CardSubtitle>{t("voice_cloning.onboarding_subtitle")}</CardSubtitle>
      <OptionCards>
        <OptionCard
          selected={isMyVoice}
          myVoice
          onClick={() => handleOptionChange(VOICE_OPTION.MY_VOICE)}
        >
          <OptionIconWrap>
            <OptionEmoji>🎙️</OptionEmoji>
          </OptionIconWrap>
          <OptionCardTitle selected={isMyVoice} myVoice>{t("voice_cloning.my_voice")}</OptionCardTitle>
          <OptionCardSubtitle>{t("voice_cloning.use_my_voice")}</OptionCardSubtitle>
        </OptionCard>

        <OptionCard
          selected={!isMyVoice}
          onClick={() => handleOptionChange(VOICE_OPTION.DEFAULT)}
        >
          <OptionIconWrap>
            <OptionEmoji>🔊</OptionEmoji>
          </OptionIconWrap>
          <OptionCardTitle selected={!isMyVoice}>{t("voice_cloning.default_voice")}</OptionCardTitle>
          <OptionCardSubtitle>{t("voice_cloning.brand_voice")}</OptionCardSubtitle>
        </OptionCard>
      </OptionCards>

      {isMyVoice && (
        <>
          <SentenceCard>
            <SentenceLabel>{t("voice_cloning.suggested_sentence")}</SentenceLabel>
            <SentenceText>
              &ldquo;{t("voice_cloning.sample_sentence")}&rdquo;
            </SentenceText>
            <SampleHint>{t("voice_cloning.sample_hint")}</SampleHint>

            {(myVoicePhase === "uploading" || myVoicePhase === "processing") && (
              <RecordArea>
                <Waveform>
                  {WAVEFORM_HEIGHTS.map((h, i) => <WaveBar key={i} height={h} index={i} />)}
                </Waveform>
                <RecordStatus>{t("voice_cloning.processing")}</RecordStatus>
                <ProcessingSubtext>{t("voice_cloning.processing_subtitle")}</ProcessingSubtext>
              </RecordArea>
            )}

            {myVoicePhase === "preview" && (
              <RecordArea>
                <Waveform>
                  {WAVEFORM_HEIGHTS.map((h, i) => <WaveBar key={i} height={h} index={i} />)}
                </Waveform>
                <PlayButton onClick={onTogglePreviewPlay}>
                  <PlayIcon>{isPreviewPlaying ? "⏸" : "▶"}</PlayIcon>
                  {t("voice_cloning.play_preview")}
                </PlayButton>
              </RecordArea>
            )}

            {myVoicePhase === "failed" && (
              <RecordArea>
                <FailedIcon>✕</FailedIcon>
                <RecordStatus>{t("voice_cloning.failed_subtitle")}</RecordStatus>
              </RecordArea>
            )}

            {!myVoicePhase && (
              isDone ? (
                <RecordArea>
                  <DoneCircle>✓</DoneCircle>
                  <RecordStatus>{t("voice_cloning.recording_ready")}</RecordStatus>
                </RecordArea>
              ) : (
                <RecordArea>
                  <MicButton
                    onClick={handleMicClick}
                    phase={phase}
                    disabled={phase === RECORDER_PHASE.COUNTDOWN || !consentChecked}
                  >
                    {phase === RECORDER_PHASE.COUNTDOWN && (
                      <CountdownNumber>{countdownValue}</CountdownNumber>
                    )}
                    {phase === RECORDER_PHASE.RECORDING && <PulseDot />}
                    {(phase === RECORDER_PHASE.IDLE || phase === RECORDER_PHASE.ERROR) && (
                      <MicSvgButton viewBox="0 0 24 24" fill="none">
                        <rect x="8" y="2" width="8" height="13" rx="4" fill="white" />
                        <path d="M5 11a7 7 0 0 0 14 0" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
                        <line x1="12" y1="18" x2="12" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        <line x1="9" y1="22" x2="15" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </MicSvgButton>
                    )}
                  </MicButton>
                  <RecordStatus>
                    {phase === RECORDER_PHASE.IDLE && t("voice_cloning.tap_to_record")}
                    {phase === RECORDER_PHASE.COUNTDOWN && t("voice_cloning.get_ready")}
                    {phase === RECORDER_PHASE.RECORDING && (
                      <>
                        {t("voice_cloning.recording")} {elapsedSeconds}s / {maxSeconds}s
                        {!canStop && <KeepSpeaking> — {t("voice_cloning.keep_speaking")}</KeepSpeaking>}
                        {canStop && <TapStop> — {t("voice_cloning.tap_to_stop")}</TapStop>}
                      </>
                    )}
                    {phase === RECORDER_PHASE.ERROR && t("voice_cloning.mic_error")}
                  </RecordStatus>
                </RecordArea>
              )
            )}
          </SentenceCard>

          <ConsentRow onClick={() => { setConsentChecked((v) => { const next = !v; onConsentChange?.(next); return next; }); }}>
            <Checkbox checked={consentChecked}>
              {consentChecked && <CheckMark>✓</CheckMark>}
            </Checkbox>
            <ConsentLabel>{t("voice_cloning.consent")}</ConsentLabel>
          </ConsentRow>

          {myVoicePhase === "preview" && (
            <PrimaryButton onClick={onSoundsGood} disabled={!consentChecked}>
              {t("voice_cloning.sounds_good")}
            </PrimaryButton>
          )}

          {(myVoicePhase === "preview" || myVoicePhase === "failed") && (
            <ReRecordButton onClick={() => { reset(); if (onReRecord) onReRecord(); }}>
              {t("voice_cloning.re_record")}
            </ReRecordButton>
          )}
        </>
      )}

      {!isMyVoice && (
        <>
          <audio
            ref={previewAudioRef}
            style={{ display: "none" }}
            onEnded={() => setPreviewingGender(null)}
            onError={() => setPreviewingGender(null)}
          />
          <GenderSubtitle>{t("voice_cloning.gender_subtitle")}</GenderSubtitle>
          <GenderCards>
            {GENDER_OPTIONS.map(({ value, emoji, labelKey }) => (
              <GenderCard
                key={value}
                selected={selectedGender === value}
                onClick={() => handleGenderChange(value)}
              >
                <GenderEmoji>{emoji}</GenderEmoji>
                <GenderLabel selected={selectedGender === value}>{t(labelKey)}</GenderLabel>
                <GenderPreviewButton
                  onClick={(e) => { e.stopPropagation(); handlePreviewGender(value); }}
                  loading={previewingGender === value}
                >
                  {previewingGender === value ? "…" : `▶ ${t("voice_cloning.preview_gender")}`}
                </GenderPreviewButton>
              </GenderCard>
            ))}
          </GenderCards>
          <ContinueButton onClick={handleContinue} disabled={isSaveable && saved}>
            {isSaveable ? (saved ? t("voice_cloning.saved") : t("voice_cloning.save")) : t("general.continue")}
          </ContinueButton>
        </>
      )}

      {onSetupLater && (
        <SetupLaterLink onClick={onSetupLater}>
          {t("voice_cloning.setup_later")}
        </SetupLaterLink>
      )}
    </Container>
  );
};

export default VoiceCloneRecorder;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55); }
  70% { box-shadow: 0 0 0 18px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

const waveAnim = keyframes`
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
`;

const WAVEFORM_HEIGHTS = [6, 14, 22, 32, 40, 28, 36, 20, 30, 40, 36, 28, 40, 32, 22, 36, 20, 28, 14, 24];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const OptionCards = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const OptionCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  border: 1.5px solid
    ${({ selected }) => (selected ? "rgba(245, 158, 11, 0.35)" : "transparent")};
  background: ${({ theme, selected }) =>
    selected ? "rgba(245, 158, 11, 0.08)" : theme.menu.groupContentBackground};
  user-select: none;
`;

const OptionIconWrap = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionEmoji = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const OptionCardTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, selected }) =>
    selected ? "#F59E0B" : theme.menu.primaryTextColor};
  transition: color 150ms ease;
`;

const OptionCardSubtitle = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const SentenceCard = styled.div`
  width: 100%;
  background: rgba(245, 158, 11, 0.08);
  border: 1.5px solid rgba(245, 158, 11, 0.35);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SentenceLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: rgba(245, 158, 11, 0.6);
  text-align: center;
`;

const SentenceText = styled.p`
  font-size: 15px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.6;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  text-align: center;
`;


const RecordArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const DoneCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #F59E0B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  font-weight: 700;
`;

const MicButton = styled.button`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: transform 120ms ease;

  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

  ${({ phase, disabled }) => {
    if (phase === RECORDER_PHASE.RECORDING) {
      return css`
        background: #F59E0B;
        animation: ${pulse} 1.4s ease-out infinite;
      `;
    }
    if (phase === RECORDER_PHASE.COUNTDOWN) {
      return css`
        background: rgba(245, 158, 11, 0.6);
      `;
    }
    return css`
      background: #F59E0B;
      ${!disabled && css`
        &:hover { transform: scale(1.06); }
        &:active { transform: scale(0.95); }
      `}
    `;
  }}
`;

const MicSvgButton = styled.svg`
  width: 26px;
  height: 26px;
`;

const CountdownNumber = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const PulseDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  animation: ${blink} 0.9s ease-in-out infinite;
`;

const RecordStatus = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
`;

const KeepSpeaking = styled.span`
  color: rgba(245, 158, 11, 0.7);
`;

const TapStop = styled.span`
  color: #F59E0B;
`;

const ReRecordButton = styled.button`
  background: none;
  border: none;
  color: #F59E0B;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
`;


const ContinueButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => (disabled ? "rgba(150,156,161,0.2)" : "#F59E0B")};
  color: ${({ disabled }) => (disabled ? "rgba(150,156,161,1)" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: background 150ms ease, color 150ms ease;
`;

const SetupLaterLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
`;

const GenderSubtitle = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
  line-height: 1.5;
`;

const GenderCards = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const GenderCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  border: 1.5px solid
    ${({ selected }) => (selected ? "rgba(245, 158, 11, 0.35)" : "transparent")};
  background: ${({ theme, selected }) =>
    selected ? "rgba(245, 158, 11, 0.08)" : theme.menu.groupContentBackground};
  user-select: none;
`;

const GenderEmoji = styled.span`
  font-size: 26px;
  line-height: 1;
`;

const GenderLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: ${({ selected, theme }) =>
    selected ? "#F59E0B" : theme.menu.primaryTextColor};
`;

const GenderPreviewButton = styled.button`
  background: none;
  border: 1px solid rgba(245, 158, 11, 0.5);
  border-radius: 6px;
  font-size: 11px;
  color: #F59E0B;
  cursor: ${({ loading }) => (loading ? "default" : "pointer")};
  padding: 3px 8px;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
  line-height: 1.5;
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  width: 100%;
  user-select: none;
`;

const Checkbox = styled.div`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid
    ${({ checked }) => (checked ? "#F59E0B" : "rgba(245, 158, 11, 0.4)")};
  background: ${({ checked }) => (checked ? "#F59E0B" : "rgba(245, 158, 11, 0.08)")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease, border-color 120ms ease;
  margin-top: 1px;
`;

const CheckMark = styled.span`
  font-size: 11px;
  color: white;
  font-weight: 700;
  line-height: 1;
`;

const ConsentLabel = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.5;
`;

const SampleHint = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
`;

const Waveform = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 44px;
`;

const WaveBar = styled.div`
  width: 3px;
  height: ${({ height }) => height}px;
  border-radius: 2px;
  background: #F59E0B;
  transform-origin: center;
  animation: ${waveAnim} ${({ index }) => 0.8 + (index % 5) * 0.15}s ease-in-out infinite;
  animation-delay: ${({ index }) => (index % 6) * 0.1}s;
`;

const ProcessingSubtext = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-align: center;
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  width: 100%;
  border-radius: 8px;
  border: 1.5px solid rgba(245, 158, 11, 0.35);
  background: transparent;
  color: #F59E0B;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease;
  &:hover { background: rgba(245, 158, 11, 0.08); }
`;

const PlayIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const ClonedText = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: rgba(72, 199, 142, 1);
`;

const ListenText = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const PreviewPlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const PreviewPlayBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #F59E0B;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const PreviewProgressWrap = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(245, 158, 11, 0.25);
  overflow: hidden;
`;

const PreviewProgressFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: #F59E0B;
  transition: width 0.25s linear;
`;

const PreviewTime = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  min-width: 28px;
  text-align: right;
`;

const PreviewReRecordRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PreviewGhostBtn = styled.button`
  background: none;
  border: none;
  color: #F59E0B;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  font-family: inherit;
`;

const PreviewBullet = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
`;

const FailedIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  font-weight: 700;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => (disabled ? "rgba(150,156,161,0.2)" : "#F59E0B")};
  color: ${({ disabled }) => (disabled ? "rgba(150,156,161,1)" : "white")};
  font-size: 15px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: background 150ms ease, color 150ms ease;
`;
